import { Injectable } from '@angular/core';
import { AccountGroup, AccountGroupApiService, ProjectionFilter } from '@galaxy/account-group';
import { BrandingV2Service } from '@galaxy/partner';
import { AccountTag, BusinessInfoService as SalesBusinessInfoService, GeneralBusinessInfo } from '@vendasta/sales-ui';
import { EMPTY, map, Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';

const ACCOUNT_INFO = (businessId: string) => `/businesses/accounts/${businessId}/details`;
const ACCOUNT_INFO_EDIT = (businessId: string) => `/businesses/accounts/${businessId}/edit`;

@Injectable()
export class BusinessInfoService implements SalesBusinessInfoService {
  constructor(
    private readonly branding: BrandingV2Service,
    private readonly accountGroupSdk: AccountGroupApiService,
    private readonly appConfigService: AppConfigService,
  ) {}

  public getBusinessData(businessId: string): Observable<GeneralBusinessInfo> {
    if (!businessId) {
      return EMPTY;
    }
    return this.accountGroupSdk
      .get(
        businessId,
        new ProjectionFilter({
          napData: true,
          contactDetails: true,
          accountGroupExternalIdentifiers: true,
          legacyProductDetails: true,
          socialUrls: true,
        }),
      )
      .pipe(
        map((ag: AccountGroup) => ({
          businessId: ag?.accountGroupId,
          name: ag?.napData?.companyName,
          partnerId: ag?.externalIdentifiers?.partnerId,
          marketId: ag?.externalIdentifiers?.marketId,
          streetAddress: ag?.napData?.address,
          city: ag?.napData?.city,
          state: ag?.napData?.state,
          countryCode: ag?.napData?.country,
          zip: ag?.napData?.zip,
          phoneNumber: ag?.contactDetails?.phoneNumber,
          website: ag?.napData?.website,
          serviceCategories: ag?.legacyProductDetails?.shareOfVoiceService || [],
          socialUrls: ag?.socialUrls,
          vCategories: ag?.externalIdentifiers?.vCategoryIds || [],
        })),
      );
  }

  public getBusinessDataUrl$(businessId: string, isEdit: boolean): Observable<string> {
    return this.appConfigService.config$.pipe(
      map((config) => {
        if (config?.userCanAccessAccounts) {
          return isEdit ? ACCOUNT_INFO_EDIT(businessId) : ACCOUNT_INFO(businessId);
        }
        return '';
      }),
    );
  }

  public getAccountTags(businessId: string): Observable<AccountTag[]> {
    return this.accountGroupSdk
      .get(
        businessId,
        new ProjectionFilter({
          accountGroupExternalIdentifiers: true,
        }),
      )
      .pipe(
        map((ag: AccountGroup) => {
          return ag?.externalIdentifiers?.tags?.map((t) => <AccountTag>{ value: t, name: t }) || [];
        }),
        shareReplay(1),
      );
  }

  public getMarketName(partnerId: string, marketId: string): Observable<string> {
    return this.branding.getBranding(partnerId, marketId).pipe(
      map((branding) => {
        return branding?.name || '';
      }),
    );
  }

  canEditCustomerIdentifier(): Observable<boolean> {
    return of(true);
  }

  canEditCustomFields(): Observable<boolean> {
    return of(true);
  }
}
